/**
 * Utility functions for presenting paper venue and reference labels in the UI.
 *
 * Canonical paper identity source ids remain internal implementation detail.
 * User-facing surfaces should use venue/host labels and durable references.
 */

const IACR_REFERENCE_PREFIX = 'IACR ePrint';

const KNOWN_SOURCE_VENUES: Record<number, string> = {
    1: 'arXiv',
    2: 'medRxiv',
    3: 'bioRxiv',
    4: 'ChinaXiv',
    5: 'PubMed',
    6: 'ChemRxiv',
};

const KNOWN_SOURCE_NAMES: Record<string, string> = {
    arxiv: 'arXiv',
    medrxiv: 'medRxiv',
    biorxiv: 'bioRxiv',
    chinaxiv: 'ChinaXiv',
    pubmed: 'PubMed',
    chemrxiv: 'ChemRxiv',
    iacr: IACR_REFERENCE_PREFIX,
    'iacr eprint': IACR_REFERENCE_PREFIX,
    ssrn: 'SSRN',
    openreview: 'OpenReview',
};

// i18n-deferred-start: taxonomy -- provider-native venue names stay authoritative
const HOST_VENUES: Array<{ suffix: string; label: string }> = [
    { suffix: 'arxiv.org', label: 'arXiv' },
    { suffix: 'medrxiv.org', label: 'medRxiv' },
    { suffix: 'biorxiv.org', label: 'bioRxiv' },
    { suffix: 'chemrxiv.org', label: 'ChemRxiv' },
    { suffix: 'eprint.iacr.org', label: IACR_REFERENCE_PREFIX },
    { suffix: 'pubmed.ncbi.nlm.nih.gov', label: 'PubMed' },
    { suffix: 'ssrn.com', label: 'SSRN' },
    { suffix: 'openreview.net', label: 'OpenReview' },
    { suffix: 'nature.com', label: 'Nature' },
];
// i18n-deferred-end

const DOI_HOSTS = new Set([
    'doi.org',
    'dx.doi.org',
    'www.doi.org',
]);

const DOI_PATTERN = /^10\.\d{4,9}\/\S+$/i;
const ARXIV_PATH_PATTERN = /arxiv\.org\/(?:abs|pdf)\/([^/?#]+)/i;
const PUBMED_PATH_PATTERN = /^\/(\d+)(?:\/|$)/;
const PMID_REFERENCE_PREFIX = 'PMID';
const ARXIV_REFERENCE_PREFIX = 'arXiv';
const DOI_REFERENCE_PREFIX = 'DOI';

export type PaperDisplayInput = {
    sourceId?: number | null;
    sourceName?: string | null;
    sourcePaperId?: string | null;
    doi?: string | null;
    url?: string | null;
    paperResolutionKey?: string | null;
};

function trimmedValue(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed || null;
}

function hostMatches(host: string, suffix: string): boolean {
    return host === suffix || host.endsWith(`.${suffix}`);
}

function parseUrl(value: string | null | undefined): URL | null {
    const normalized = trimmedValue(value);
    if (!normalized) {
        return null;
    }

    try {
        return new URL(normalized);
    } catch {
        return null;
    }
}

function normalizeDoi(value: string | null | undefined): string | null {
    const trimmed = trimmedValue(value);
    if (!trimmed) {
        return null;
    }

    const withoutUrlPrefix = trimmed.replace(/^https?:\/\/(?:dx\.)?doi\.org\//i, '');
    const withoutDoiPrefix = withoutUrlPrefix.replace(/^doi:/i, '');
    return DOI_PATTERN.test(withoutDoiPrefix) ? withoutDoiPrefix : null;
}

function urlVenue(url: string | null | undefined): string | null {
    const parsed = parseUrl(url);
    if (!parsed) {
        return null;
    }

    const host = parsed.hostname.toLowerCase();
    if (DOI_HOSTS.has(host)) {
        return null;
    }

    const match = HOST_VENUES.find(({ suffix }) => hostMatches(host, suffix));
    return match?.label ?? null;
}

function sourcePaperIdUrl(sourcePaperId: string | null | undefined): string | null {
    const normalized = trimmedValue(sourcePaperId);
    if (!normalized) {
        return null;
    }

    if (normalized.startsWith('url:') || normalized.startsWith('pdf:')) {
        return normalized.slice(4);
    }

    return null;
}

export function getPaperVenueLabel({
    sourceId,
    sourceName,
    sourcePaperId,
    url,
}: PaperDisplayInput): string | null {
    if (sourceId && KNOWN_SOURCE_VENUES[sourceId]) {
        return KNOWN_SOURCE_VENUES[sourceId];
    }

    const normalizedSourceName = trimmedValue(sourceName)?.toLowerCase();
    if (normalizedSourceName && KNOWN_SOURCE_NAMES[normalizedSourceName]) {
        return KNOWN_SOURCE_NAMES[normalizedSourceName];
    }

    return urlVenue(url) || urlVenue(sourcePaperIdUrl(sourcePaperId));
}

export function getSourceVenueLabels(sourceIds: readonly number[]): string[] {
    const labels = sourceIds
        .map((sourceId) => getPaperVenueLabel({ sourceId }))
        .filter((label): label is string => Boolean(label));

    return [...new Set(labels)];
}

export function getPaperArxivId({
    sourceId,
    sourceName,
    sourcePaperId,
    url,
    paperResolutionKey,
}: PaperDisplayInput): string | null {
    const resolutionKey = trimmedValue(paperResolutionKey);
    if (resolutionKey?.toLowerCase().startsWith('arxiv:')) {
        return resolutionKey.slice('arxiv:'.length);
    }

    const normalizedSourcePaperId = trimmedValue(sourcePaperId);
    if (normalizedSourcePaperId) {
        if (normalizedSourcePaperId.toLowerCase().startsWith('arxiv:')) {
            return normalizedSourcePaperId.slice('arxiv:'.length);
        }

        if (sourceId === 1 || trimmedValue(sourceName)?.toLowerCase() === 'arxiv') {
            return normalizedSourcePaperId;
        }
    }

    const candidateUrl = sourcePaperIdUrl(sourcePaperId) || url;
    const match = (candidateUrl || '').match(ARXIV_PATH_PATTERN);
    if (!match?.[1]) {
        return null;
    }

    return match[1].replace(/\.pdf$/i, '');
}

export function getPaperDoi({
    doi,
    sourcePaperId,
    url,
}: PaperDisplayInput): string | null {
    const directDoi = normalizeDoi(doi);
    if (directDoi) {
        return directDoi;
    }

    const sourcePaperDoi = normalizeDoi(sourcePaperId);
    if (sourcePaperDoi) {
        return sourcePaperDoi;
    }

    const parsedUrl = parseUrl(url);
    if (!parsedUrl || !DOI_HOSTS.has(parsedUrl.hostname.toLowerCase())) {
        return null;
    }

    return normalizeDoi(parsedUrl.toString());
}

export function getPaperPmid({
    sourceId,
    sourceName,
    sourcePaperId,
    url,
}: PaperDisplayInput): string | null {
    const normalizedSourcePaperId = trimmedValue(sourcePaperId);
    const sourceIsPubmed = sourceId === 5 || trimmedValue(sourceName)?.toLowerCase() === 'pubmed';
    if (sourceIsPubmed && normalizedSourcePaperId) {
        const withoutPrefix = normalizedSourcePaperId.replace(/^pmid:\s*/i, '');
        if (/^\d+$/.test(withoutPrefix)) {
            return withoutPrefix;
        }
    }

    const parsedUrl = parseUrl(sourcePaperIdUrl(sourcePaperId) || url);
    if (!parsedUrl || !hostMatches(parsedUrl.hostname.toLowerCase(), 'pubmed.ncbi.nlm.nih.gov')) {
        return null;
    }
    const match = parsedUrl.pathname.match(PUBMED_PATH_PATTERN);
    return match?.[1] || null;
}

export function getPaperReferenceLabel(input: PaperDisplayInput): string | null {
    if (getPaperVenueLabel(input) === IACR_REFERENCE_PREFIX) {
        const sourceId = input.sourcePaperId?.trim() || '';
        const pathId = parseUrl(input.url)?.pathname.replace(/^\/|\/$/g, '').replace(/\.pdf$/, '') || '';
        const identifier = /^\d{4}\/\d+$/.test(sourceId) ? sourceId : pathId;
        if (/^\d{4}\/\d+$/.test(identifier)) return `${IACR_REFERENCE_PREFIX} ${identifier}`;
    }

    const pmid = getPaperPmid(input);
    if (pmid) {
        return `${PMID_REFERENCE_PREFIX} ${pmid}`;
    }

    const arxivId = getPaperArxivId(input);
    if (arxivId) {
        return `${ARXIV_REFERENCE_PREFIX} ${arxivId}`;
    }

    const doi = getPaperDoi(input);
    if (doi) {
        return `${DOI_REFERENCE_PREFIX} ${doi}`;
    }

    return getPaperVenueLabel(input);
}

export function registerIacrSource(sourceId: number): void {
    if (Number.isInteger(sourceId) && sourceId > 0 && !KNOWN_SOURCE_VENUES[sourceId]) {
        KNOWN_SOURCE_VENUES[sourceId] = IACR_REFERENCE_PREFIX;
    }
}

export function getSourceName(sourceId: number | null | undefined): string | null {
    return getPaperVenueLabel({ sourceId });
}
